/**
 * pages/Brief.js — the in-app "edition mirror" of the weekly email.
 *
 * Deep-link target for the email `Open in 2120 →` CTA. Renders the same
 * five sections (Celebrate / Watch / Coming Up / Progress / Question)
 * plus an inline capture textarea for the Question of the Week. The
 * answer saves to `weekly_reflections` via /api/weekly-reflections.
 *
 * Design goals:
 *   • No modal jail — the textarea is inline; typing is optional.
 *   • Renders even when there's no signal (WEAK/STALE/OFF states
 *     surface a shorter card set from the same endpoint).
 *   • Reuses Shadcn primitives (Card, Textarea, Button, Skeleton) so
 *     it inherits the whole app's theme + dark mode.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { BookOpen, Sparkles, TrendingUp, Calendar, Target, MessageCircleQuestion, ExternalLink, Check } from 'lucide-react';
import usePageTitle from '@/hooks/usePageTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ANSWER_SOFT_CAP = 280;    // soft — UI counter only; server allows 2000

const ACCENT = {
  positive: { chip: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300', dot: 'bg-emerald-500' },
  warning:  { chip: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',         dot: 'bg-amber-500'   },
  info:     { chip: 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',                 dot: 'bg-sky-500'     },
  neutral:  { chip: 'bg-muted text-muted-foreground',                                              dot: 'bg-muted-foreground/40' },
};

const SECTION_META = {
  celebrate: { icon: Sparkles,               label: 'Celebrate',      emoji: '🌿' },
  watch:     { icon: TrendingUp,             label: 'Watch',          emoji: '👀' },
  coming_up: { icon: Calendar,               label: 'Coming up',      emoji: '📅' },
  progress:  { icon: Target,                 label: 'Progress',       emoji: '🎯' },
  question:  { icon: MessageCircleQuestion,  label: 'This week’s question', emoji: '💭' },
};

export default function Brief() {
  usePageTitle('This Week');
  const { session } = useAuth();
  const token = session?.access_token;

  const [loading, setLoading]       = useState(true);
  const [brief, setBrief]           = useState(null);
  const [subject, setSubject]       = useState('');
  const [weekEnding, setWeekEnding] = useState('');

  const [reflection, setReflection]   = useState(null);        // { question_text, answer_text, answered, ... }
  const [answer, setAnswer]           = useState('');
  const [saving, setSaving]           = useState(false);
  const [savedFlash, setSavedFlash]   = useState(false);
  const initialAnswerRef              = useRef('');

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [p, r] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/weekly-brief/preview?include_html=false`,
                    { headers: { Authorization: `Bearer ${token}` }, timeout: 20000 }),
          axios.get(`${BACKEND_URL}/api/weekly-reflections/current`,
                    { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 }),
        ]);
        if (cancelled) return;
        setBrief(p.data?.brief || null);
        setSubject(p.data?.subject || '');
        setWeekEnding(p.data?.week_ending || '');
        const rf = r.data?.reflection || null;
        setReflection(rf);
        const initial = rf?.answer_text || '';
        setAnswer(initial);
        initialAnswerRef.current = initial;
      } catch (e) {
        console.warn('Brief load failed:', e?.message);
        toast.error('Could not load this week’s brief.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const dirty = useMemo(() => (answer || '').trim() !== (initialAnswerRef.current || '').trim(), [answer]);

  const saveReflection = async () => {
    const text = (answer || '').trim();
    if (!text) { toast.info('Type a few words before saving.'); return; }
    if (!token) return;
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/weekly-reflections`,
        { week_ending: weekEnding || undefined, answer_text: text },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 });
      const rf = res.data?.reflection;
      if (rf) {
        setReflection(rf);
        initialAnswerRef.current = rf.answer_text || '';
      }
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
      toast.success('Reflection saved.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save reflection.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="brief-loading">
        <Skeleton className="h-16 rounded-2xl" />
        <Skeleton className="h-24 rounded-2xl" />
        <Skeleton className="h-24 rounded-2xl" />
        <Skeleton className="h-48 rounded-3xl" />
      </div>
    );
  }

  if (!brief) {
    return (
      <Card className="rounded-3xl border-dashed" data-testid="brief-empty">
        <CardContent className="p-10 text-center">
          <BookOpen className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
          <div className="font-display text-lg font-semibold">This week’s brief isn’t ready yet</div>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            Check back Sunday morning — your next edition will land here (and in your inbox).
          </p>
        </CardContent>
      </Card>
    );
  }

  const editionLabel = brief.edition_label || '';
  const sections = ['celebrate', 'watch', 'coming_up', 'progress'];

  return (
    <div className="space-y-5" data-testid="brief-root">
      {/* Header */}
      <div>
        <div className="text-[11px] uppercase tracking-[2px] text-gold" data-testid="brief-edition-label">{editionLabel}</div>
        <h1 className="font-display text-2xl font-semibold mt-1">This Week In Your Money</h1>
        <p className="text-sm text-muted-foreground mt-1">Here’s what mattered in your money this week.</p>
      </div>

      {/* Data sections */}
      <div className="grid gap-3 md:grid-cols-2">
        {sections.map((k) => {
          const s = brief[k];
          if (!s) return null;
          const meta = SECTION_META[k];
          const accent = ACCENT[s.accent || 'neutral'] || ACCENT.neutral;
          const Icon = meta.icon;
          return (
            <Card key={k} className="rounded-2xl border-border/70" data-testid={`brief-section-${k}`}>
              <CardContent className="p-5">
                <div className="flex items-center gap-2">
                  <div className={`h-2 w-2 rounded-full ${accent.dot}`} />
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                    <Icon className="h-3 w-3" /> {meta.label}
                  </div>
                </div>
                <div className="font-medium mt-2" data-testid={`brief-section-${k}-headline`}>{s.headline}</div>
                {s.teaser && <div className="text-sm text-muted-foreground mt-1 leading-relaxed">{s.teaser}</div>}
                {Array.isArray(s.items) && s.items.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`brief-section-${k}-items`}>
                    {s.items.slice(0, 6).map((it, i) => (
                      <Badge key={i} variant="secondary" className="rounded-full text-[11px] font-normal">
                        {it.label || it.name || it.title || String(it)}
                        {typeof it.amount === 'number' ? ` · ${it.amount}` : ''}
                      </Badge>
                    ))}
                  </div>
                )}
                {typeof s.progress_pct === 'number' && (
                  <div className="mt-3">
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className="h-full bg-primary transition-all" style={{ width: `${Math.max(0, Math.min(100, s.progress_pct))}%` }} />
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1 tabular-nums">{Math.round(s.progress_pct)}%</div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Question of the week (inline reflection capture) */}
      <Card className="rounded-3xl border-0 bg-brand-gradient text-primary-foreground shadow-brand-lg" data-testid="brief-question-card">
        <CardContent className="p-6 sm:p-8 space-y-4">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[2px] text-gold">
            <MessageCircleQuestion className="h-3.5 w-3.5" /> Question of the week
          </div>
          <div className="font-display text-xl leading-snug" data-testid="brief-question-text">
            {reflection?.question_text || brief.question?.headline || '—'}
          </div>
          <div>
            <Textarea
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="A few sentences — no reply needed, just for you."
              rows={4}
              className="bg-white/10 border-white/20 text-primary-foreground placeholder:text-primary-foreground/50 rounded-xl focus-visible:ring-2 focus-visible:ring-gold"
              data-testid="brief-answer-textarea"
            />
            <div className="flex items-center justify-between mt-2">
              <div className="text-[11px] text-primary-foreground/60 tabular-nums">
                {answer.length}/{ANSWER_SOFT_CAP} chars
              </div>
              <div className="flex items-center gap-2">
                {savedFlash && (
                  <span className="text-[11px] text-emerald-300 flex items-center gap-1">
                    <Check className="h-3 w-3" /> Saved
                  </span>
                )}
                <Button
                  onClick={saveReflection}
                  disabled={saving || !dirty}
                  className="rounded-xl bg-gold text-primary hover:bg-gold/90"
                  data-testid="brief-answer-save"
                >
                  {saving ? 'Saving…' : reflection?.answered ? 'Update reflection' : 'Save reflection'}
                </Button>
              </div>
            </div>
          </div>
          {reflection?.updated_at && !dirty && (
            <div className="text-[11px] text-primary-foreground/60 pt-1">
              Saved {new Date(reflection.updated_at).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Footer link to reflections journal */}
      <div className="text-center pt-1">
        <a
          href="/reflections"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          data-testid="brief-reflections-link"
        >
          See your past reflections <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
    </div>
  );
}
